// components/onboarding/OnboardingGate.jsx
//
// Action-time onboarding gate. Instead of blocking a whole route (the old BusinessOpGuard behaviour),
// order screens stay fully usable; a page calls `useOnboardingGate().requireBusinessOps(label)` in its
// submit handler. If business ops are locked, that returns false and opens the guided wizard as an
// OVERLAY on top of the page - so nothing the user typed into the order is lost.
//
// The overlay is honest about backend verification timing: after the user submits for verification it
// reflects the real gate state (unlocked / under review / agreement pending) rather than assuming an
// instant unlock.

import React, { createContext, useCallback, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useOnboardingProgress from '../../hooks/useOnboardingProgress';
import { signOnboardingAgreement } from '../../services/onboardingProgressService';
import OnboardingWizard from './wizard/OnboardingWizard';
import { SuccessBurst, AnimatedCheck } from './OnboardingDecor';

const OnboardingGateContext = createContext(null);

// Defensive default so pages never crash if the provider isn't mounted (fails open - backend still enforces).
export function useOnboardingGate() {
  return useContext(OnboardingGateContext) || { requireBusinessOps: () => true };
}

const CloseIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clipRule="evenodd" /></svg>
);

function Panel({ tone = 'blue', icon, title, celebrate, children }) {
  const bar = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-emerald-500 to-green-600',
    amber: 'from-amber-500 to-orange-600',
  }[tone];
  return (
    <div className="relative overflow-hidden p-6 sm:p-8">
      {celebrate && <SuccessBurst />}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
        className={`relative mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${bar} text-2xl text-white shadow-md`}
        aria-hidden="true"
      >
        {icon}
      </motion.div>
      <h2 className="relative text-xl font-bold text-gray-900">{title}</h2>
      <div className="relative mt-2 text-[15px] text-gray-600">{children}</div>
    </div>
  );
}

function GateModalBody({ actionLabel, onClose }) {
  const navigate = useNavigate();
  const merchantId = useSelector((s) => s.auth?.merchantId) || localStorage.getItem('merchantId') || null;
  const { businessOpsUnlocked, status, agreementRequired, agreementSigned, refetch } = useOnboardingProgress();
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);

  const agreementPending = agreementRequired && !agreementSigned;
  const unlocked = businessOpsUnlocked === true;
  const underReview = status === 'PENDING_VERIFICATION' || justSubmitted;

  const handleSign = async () => {
    if (!merchantId) { setSignError('We could not determine your account. Please refresh and try again.'); return; }
    setSigning(true); setSignError(null);
    try {
      await signOnboardingAgreement(merchantId);
      if (refetch) await refetch();
    } catch (err) {
      setSignError(err?.response?.data?.message || err?.message || 'Failed to sign the agreement. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const PrimaryBtn = ({ children, onClick, tone = 'blue' }) => (
    <button
      type="button" onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all ${
        tone === 'green' ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700'
        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
      }`}
    >
      {children}
    </button>
  );

  // 1) Became unlocked - let them proceed with their order.
  if (unlocked) {
    return (
      <Panel tone="green" celebrate icon={<AnimatedCheck size={30} className="text-white" />} title="You’re all set!">
        <p>Your account is active. Your order details are exactly as you left them - go ahead and place your order.</p>
        <div className="mt-5"><PrimaryBtn tone="green" onClick={onClose}>Continue to my order →</PrimaryBtn></div>
      </Panel>
    );
  }

  // 2) Agreement pending - sign to activate.
  if (agreementPending) {
    return (
      <Panel tone="amber" icon="✍️" title="Sign your agreement to continue">
        <p>Your merchant agreement is ready. Review it in <button className="font-semibold text-blue-600 hover:underline" onClick={() => { onClose(); navigate('/profile'); }}>Profile → Documents</button> and sign to activate your account.</p>
        {signError && <p className="mt-3 text-sm text-red-600">{signError}</p>}
        <div className="mt-5 flex flex-wrap gap-2">
          <PrimaryBtn onClick={handleSign}>{signing ? 'Signing…' : 'I have reviewed & agree - Sign'}</PrimaryBtn>
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-100">Not now</button>
        </div>
      </Panel>
    );
  }

  // 3) Submitted / under review - reassure and nudge Save as Draft.
  if (underReview) {
    return (
      <Panel tone="blue" celebrate icon="⏳" title="Your details are in - verification in progress">
        <p>Thanks! We’re verifying your account and will notify you by email and in-app once it’s approved, usually within a few hours.</p>
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Tip: use <span className="font-semibold">Save as draft</span> on this order so it’s ready to submit the moment you’re verified.
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <PrimaryBtn onClick={() => { onClose(); navigate('/all-shipments'); }}>Go to dashboard</PrimaryBtn>
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-100">Back to my order</button>
        </div>
      </Panel>
    );
  }

  // 4) Default - the guided wizard, inline, preserving the order behind it.
  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 px-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Almost there</p>
        <h2 className="text-xl font-bold text-gray-900">Finish setup to {actionLabel}</h2>
        <p className="mt-1 text-sm text-gray-500">Complete the quick steps below. We’ve kept your order details safe in the background.</p>
      </div>
      <OnboardingWizard variant="overlay" onExit={onClose} onComplete={() => setJustSubmitted(true)} />
    </div>
  );
}

export function OnboardingGateProvider({ children }) {
  const { businessOpsUnlocked, status } = useOnboardingProgress();
  const [gate, setGate] = useState({ open: false, actionLabel: 'this action' });

  // Allowed when: definitively unlocked; the gate state is still unknown (fail open - backend
  // enforces authoritatively); OR the merchant has already submitted for verification. Submitted
  // (under review) and verified accounts may place orders while review is in progress.
  const requireBusinessOps = useCallback((actionLabel = 'this action') => {
    const submitted = status === 'PENDING_VERIFICATION' || status === 'VERIFIED';
    if (businessOpsUnlocked !== false || submitted) return true;
    setGate({ open: true, actionLabel });
    return false;
  }, [businessOpsUnlocked, status]);

  const close = useCallback(() => setGate((g) => ({ ...g, open: false })), []);

  return (
    <OnboardingGateContext.Provider value={{ requireBusinessOps }}>
      {children}
      <AnimatePresence>
        {gate.open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/60 p-3 backdrop-blur-sm sm:items-center sm:p-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
            role="dialog" aria-modal="true"
          >
            <motion.div
              className="relative my-auto w-full max-w-3xl rounded-3xl bg-white shadow-2xl"
              initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <button
                type="button" onClick={close} aria-label="Close"
                className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-gray-500 shadow-sm hover:bg-gray-100 hover:text-gray-700"
              >
                <CloseIcon />
              </button>
              <GateModalBody actionLabel={gate.actionLabel} onClose={close} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </OnboardingGateContext.Provider>
  );
}

export default OnboardingGateProvider;
